import { useState, useEffect } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { Box, Typography, Button, Alert, Card, CardContent } from "@mui/material";
import buildRoute from "components/buildRoute";
import PageHeader from "components/PageHeader";
import JobFormFields, { validateJob, jobNullableFields, jobInitialData } from "../components/JobFormFields";
import ClientFormFields, { validateClient, clientNullableFields, clientPhoneFields, clientInitialData } from "../components/ClientFormFields";
import ClientContactFormFields, { validateClientContact, clientContactNullableFields, clientContactPhoneFields, clientContactInitialData } from "../components/ClientContactFormFields";
import AnalystFormFields, { validateAnalyst, analystNullableFields, analystInitialData } from "../components/AnalystFormFields";
import SessionFormFields, { validateSession, sessionNullableFields, sessionInitialData } from "../components/SessionFormFields";
import FacilityFormFields, { validateFacility, facilityNullableFields, facilityPhoneFields, facilityInitialData } from "../components/FacilityFormFields";
import VendorFormFields, { validateVendor, vendorNullableFields, vendorPhoneFields, vendorInitialData } from "../components/VendorFormFields";
import FinanceFormFields, { validateFinance, financeNullableFields, financeInitialData } from "../components/FinanceFormFields";
import { createRecord, cleanFormData, canCreateRecordType } from "../hartUtils";
import { getCsrfToken } from "utils/csrf";
import { trackEvent } from "../../../configureMixpanel";

export const AddNewDetailsPage = () => {
  const { projectId, tableType } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Get source context from query params (when adding from a detail page)
  const sourceTable = searchParams.get('sourceTable');
  const sourceId = searchParams.get('sourceId');
  const associationType = searchParams.get('associationType');

  /**
   * Configuration for each record type
   * To add a new record type:
   * 1. Add display label to RECORD_TYPE_LABELS
   * 2. Add initial form data to INITIAL_FORM_DATA
   * 3. Add validation function to VALIDATION_FUNCTIONS
   * 4. Add nullable fields to NULLABLE_FIELDS
   * 5. Add form component to FORM_COMPONENTS
   */

  // Map plural route names to singular display names
  const RECORD_TYPE_LABELS = {
    jobs: 'Job',
    sessions: 'Session',
    facilities: 'Facility',
    vendors: 'Vendor',
    finances: 'Finance',
    clients: 'Client',
    client_contacts: 'Client Contact',
    analysts: 'Analyst',
  };

  const recordLabel = RECORD_TYPE_LABELS[tableType] || tableType?.charAt(0).toUpperCase() + tableType?.slice(1);

  // Initial form data structures for each record type
  // Imported from form components
  const INITIAL_FORM_DATA = {
    jobs: jobInitialData,
    sessions: sessionInitialData,
    facilities: facilityInitialData,
    vendors: vendorInitialData,
    finances: financeInitialData,
    clients: clientInitialData,
    client_contacts: clientContactInitialData,
    analysts: analystInitialData,
  };

  // Form components for each record type
  const FORM_COMPONENTS = {
    jobs: JobFormFields,
    sessions: SessionFormFields,
    facilities: FacilityFormFields,
    vendors: VendorFormFields,
    finances: FinanceFormFields,
    clients: ClientFormFields,
    client_contacts: ClientContactFormFields,
    analysts: AnalystFormFields,
  };

  // Validation functions for each record type
  const VALIDATION_FUNCTIONS = {
    jobs: validateJob,
    sessions: validateSession,
    facilities: validateFacility,
    vendors: validateVendor,
    finances: validateFinance,
    clients: validateClient,
    client_contacts: validateClientContact,
    analysts: validateAnalyst,
  };

  const [formData, setFormData] = useState(INITIAL_FORM_DATA[tableType] || {});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});

  // Prepopulate form fields from URL params (e.g., job_no when adding session from job page)
  useEffect(() => {
    const prepopulateFromSource = async () => {
      // SESSION + JOB: Prepopulate job_no field
      if (tableType === 'sessions' && sourceTable === 'job' && sourceId) {
        try {
          // Fetch job data to get both job_no and job_name
          const response = await fetch(`/hart/jobs/${sourceId}`);
          if (response.ok) {
            const jobData = await response.json();
            setFormData(prev => ({
              ...prev,
              job_no: jobData.job_no,
              job_name: jobData.job_name,
            }));
          }
        } catch (err) {
          console.error('Error fetching job for prepopulation:', err);
        }
      }

      // CLIENT_CONTACT + CLIENT: Prepopulate client field
      if (tableType === 'client_contacts' && sourceTable === 'client' && sourceId) {
        try {
          // Fetch client data to get both org_id and name
          const response = await fetch(`/hart/clients/${sourceId}`);
          if (response.ok) {
            const clientData = await response.json();
            setFormData(prev => ({
              ...prev,
              client: clientData.org_id,
              client_name: clientData.name,
            }));
          }
        } catch (err) {
          console.error('Error fetching client for prepopulation:', err);
        }
      }
    };

    prepopulateFromSource();
  }, [tableType, sourceTable, sourceId]);

  // Fields that should be converted from empty string to null before sending to backend
  const NULLABLE_FIELDS = {
    jobs: jobNullableFields,
    sessions: sessionNullableFields,
    facilities: facilityNullableFields,
    vendors: vendorNullableFields,
    finances: financeNullableFields,
    clients: clientNullableFields,
    client_contacts: clientContactNullableFields,
    analysts: analystNullableFields,
  };

  const PHONE_FIELDS = {
    jobs: [],
    sessions: [],
    facilities: facilityPhoneFields,
    vendors: vendorPhoneFields,
    finances: [],
    clients: clientPhoneFields,
    client_contacts: clientContactPhoneFields,
    analysts: [],
  };

  const INTEGER_FIELDS = {
    jobs: [],
    sessions: ['session_id'],
    facilities: [],
    vendors: [],
    finances: [],
    clients: [],
    client_contacts: [],
    analysts: [],
  };

  // Map table types to their ID field names for navigation
  const ID_FIELD_MAP = {
    jobs: 'job_no',
    sessions: 'session_id',
    facilities: 'facility_id',
    vendors: 'vendor_id',
    finances: 'id',
    clients: 'org_id',
    client_contacts: 'id',
    analysts: 'id',
  };

  // Map of dropdown fields (ForeignKey/ManyToMany) for better error messages
  const DROPDOWN_FIELDS = {
    jobs: {
      job_type: 'Job Type',
      job_category: 'Job Category',
    },
    sessions: {
      sess_type: 'Session Type',
      mode: 'Mode',
      sample_type: 'Sample Type',
      issue_type: 'Issue Type',
      fg_type: 'FG Type',
      fg_geography: 'FG Geography',
      fg_topic: 'FG Topic',
    },
    facilities: {},
    vendors: {
      type: 'Vendor Type',
    },
    clients: {
      sector: 'Sector',
    },
    analysts: {},
  };

  /**
   * Create association between the newly created record and the source record
   */
  const createAssociation = async (sourceTable, sourceId, newRecordTable, newRecordId) => {
    try {
      // JOB + ANALYST: Add analyst to job's analysts array via PATCH
      if (sourceTable === 'job' && newRecordTable === 'analysts') {
        // Fetch current job to get existing analysts
        const jobResponse = await fetch(`/hart/jobs/${sourceId}`);
        const jobData = await jobResponse.json();

        // Add new analyst to the list
        const updatedAnalysts = [
          ...(jobData.analysts || []),
          {
            id: newRecordId,
            lead: false,
            notes: ''
          }
        ];

        // PATCH job with updated analysts
        await fetch(`/hart/jobs/${sourceId}`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': getCsrfToken(),
          },
          body: JSON.stringify({ analysts: updatedAnalysts })
        });
      }

      // JOB + CLIENT: Add client to job's clients array via PATCH
      else if (sourceTable === 'job' && newRecordTable === 'clients') {
        // Fetch current job to get existing clients
        const jobResponse = await fetch(`/hart/jobs/${sourceId}`);
        const jobData = await jobResponse.json();

        // Get existing client IDs from the clients array
        const existingClientIds = (jobData.clients || []).map(c => c.id);

        // Add new client to the list
        const updatedClients = [...existingClientIds, newRecordId];

        // PATCH job with updated clients
        await fetch(`/hart/jobs/${sourceId}`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': getCsrfToken(),
          },
          body: JSON.stringify({ client: updatedClients })
        });
      }

      // JOB + SESSION: Set session's job FK via PATCH
      else if (sourceTable === 'job' && newRecordTable === 'sessions') {
        await fetch(`/hart/sessions/${newRecordId}`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': getCsrfToken(),
          },
          body: JSON.stringify({ job: sourceId })
        });
      }

      // FACILITY + SESSION: Set session's facility FK via PATCH
      else if (sourceTable === 'facility' && newRecordTable === 'sessions') {
        await fetch(`/hart/sessions/${newRecordId}`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': getCsrfToken(),
          },
          body: JSON.stringify({ facility_id: sourceId })
        });
      }

      // VENDOR + SESSION: Set session's vendor FK via PATCH
      else if (sourceTable === 'vendor' && newRecordTable === 'sessions') {
        await fetch(`/hart/sessions/${newRecordId}`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': getCsrfToken(),
          },
          body: JSON.stringify({ vendor_id: sourceId })
        });
      }

      // SESSION + VENDOR: Set session's vendor FK via PATCH
      else if (sourceTable === 'session' && newRecordTable === 'vendors') {
        await fetch(`/hart/sessions/${sourceId}`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': getCsrfToken(),
          },
          body: JSON.stringify({ vendor_id: newRecordId })
        });
      }

      // SESSION + FACILITY: Set session's facility FK via PATCH
      else if (sourceTable === 'session' && newRecordTable === 'facilities') {
        await fetch(`/hart/sessions/${sourceId}`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': getCsrfToken(),
          },
          body: JSON.stringify({ facility_id: newRecordId })
        });
      }

      // CLIENT + CLIENT_CONTACT: Set contact's client FK via PATCH
      else if (sourceTable === 'client' && newRecordTable === 'client_contacts') {
        await fetch(`/hart/client_contacts/${newRecordId}`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': getCsrfToken(),
          },
          body: JSON.stringify({ client: sourceId })
        });
      }

      // CLIENT + JOB: Add client to job's clients array via PATCH
      else if (sourceTable === 'client' && newRecordTable === 'jobs') {
        // Fetch the newly created job to get its current clients (if any)
        const jobResponse = await fetch(`/hart/jobs/${newRecordId}`);
        const jobData = await jobResponse.json();

        // Get existing client IDs
        const existingClientIds = (jobData.clients || []).map(c => c.id);

        // Add source client to the list
        const updatedClients = [...existingClientIds, sourceId];

        // PATCH job with updated clients
        await fetch(`/hart/jobs/${newRecordId}`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': getCsrfToken(),
          },
          body: JSON.stringify({ client: updatedClients })
        });
      }

      // ANALYST + JOB: Add analyst to job's analysts array via PATCH
      else if (sourceTable === 'analyst' && newRecordTable === 'jobs') {
        // Fetch the newly created job to get its current analysts (if any)
        const jobResponse = await fetch(`/hart/jobs/${newRecordId}`);
        const jobData = await jobResponse.json();

        // Add source analyst to the list
        const updatedAnalysts = [
          ...(jobData.analysts || []),
          {
            id: sourceId,
            lead: false,
            notes: ''
          }
        ];

        // PATCH job with updated analysts
        await fetch(`/hart/jobs/${newRecordId}`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': getCsrfToken(),
          },
          body: JSON.stringify({ analysts: updatedAnalysts })
        });
      }

      // SESSION + VENDOR: Set session's vendor FK via PATCH
      else if (sourceTable === 'session' && newRecordTable === 'vendors') {
        await fetch(`/hart/sessions/${sourceId}`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': getCsrfToken(),
          },
          body: JSON.stringify({ vendor_id: newRecordId })
        });
      }

      // SESSION + FACILITY: Set session's facility FK via PATCH
      else if (sourceTable === 'session' && newRecordTable === 'facilities') {
        await fetch(`/hart/sessions/${sourceId}`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': getCsrfToken(),
          },
          body: JSON.stringify({ facility_id: newRecordId })
        });
      }

      return { success: true };

    } catch (error) {
      console.error('Error creating association:', error);
      return { success: false, error: error.message };
    }
  };

  const handleSave = async (redirectPath) => {
    setSaving(true);
    setError(null);
    setSuccess(false);
    setValidationErrors({});

    try {
      // Validate form if validation function exists
      const validateFn = VALIDATION_FUNCTIONS[tableType];
      if (validateFn) {
        const errors = tableType === 'sessions'
          ? validateFn(formData, true)
          : validateFn(formData);
        if (Object.keys(errors).length > 0) {
          setValidationErrors(errors);

          // Check if errors are for dropdown fields
          const dropdownFields = DROPDOWN_FIELDS[tableType] || {};
          const dropdownFieldErrors = Object.keys(errors).filter(field => dropdownFields[field]);

          if (dropdownFieldErrors.length > 0) {
            const fieldNames = dropdownFieldErrors.map(field => dropdownFields[field]).join(', ');
            throw new Error(
              `The following dropdown fields are not currently available and cannot be populated: ${fieldNames}. ` +
              'Please leave these fields empty or contact your administrator to set up the dropdown options.'
            );
          } else {
            throw new Error('Please fix the validation errors before saving');
          }
        }
      }

      // Clean up data - convert empty strings to null for nullable fields
      let cleanedData = cleanFormData(
        formData,
        NULLABLE_FIELDS[tableType] || [],
        PHONE_FIELDS[tableType] || [],
        INTEGER_FIELDS[tableType] || []
      );

      // Transform payload for sessions
      if (tableType === 'sessions') {
        const { job_no, job_name, session_id, ...restData } = cleanedData;
        cleanedData = {
          ...restData,
          job: job_no,
        };
        // session_id is omitted — backend auto-generates it
      }

      const savedData = await createRecord(`/hart/${tableType}`, cleanedData);
      setSuccess(true);
      trackEvent("hart_record_created", {
        record_type: tableType,
        via_association: Boolean(sourceTable && sourceId),
        success: true,
      });

      // If we have source context, create the association
      if (sourceTable && sourceId) {
        const idField = ID_FIELD_MAP[tableType];
        const newRecordId = savedData[idField];

        const associationResult = await createAssociation(
          sourceTable,
          sourceId,
          tableType,
          newRecordId
        );

        if (!associationResult.success) {
          console.warn('Failed to create association:', associationResult.error);
        }

        // Special handling for host_recruiter: create HostDetails or RecruiterDetails
        if (associationType === 'host_recruiter' && tableType === 'vendors' && sourceTable === 'session') {
          try {
            // Fetch the newly created vendor to get its type
            const vendorResponse = await fetch(`/hart/vendors/${newRecordId}`);
            const vendorData = await vendorResponse.json();
            const vendorType = vendorData.type;

            // Create HostDetails or RecruiterDetails based on vendor type
            if (vendorType === 'Host') {
              await fetch('/hart/host_details', {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  'X-CSRFToken': getCsrfToken(),
                },
                body: JSON.stringify({
                  session_id: sourceId,
                  vendor_id: newRecordId,
                  notes: ''
                })
              });
            } else if (vendorType === 'Recruiter') {
              await fetch('/hart/recruiter_details', {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  'X-CSRFToken': getCsrfToken(),
                },
                body: JSON.stringify({
                  session_id: sourceId,
                  vendor_id: newRecordId,
                  notes: ''
                })
              });
            }
          } catch (err) {
            console.error('Error creating host/recruiter details:', err);
          }
        }
      }

      // Navigate to appropriate page after successful save
      setTimeout(() => {
        if (redirectPath === 'details') {
          const idField = ID_FIELD_MAP[tableType];
          const recordId = savedData[idField];

          // Redirect back to source page if we came from there, otherwise to new record
          navigate(`/projects/${projectId}/hart/${tableType}/${recordId}`, { replace: true });
        } else if (redirectPath === 'addNew') {
          navigate(`/projects/${projectId}/hart/new`);
        }
      }, 1500);

    } catch (err) {
      console.error('Error saving record:', err);
      trackEvent("hart_record_created", {
        record_type: tableType,
        via_association: Boolean(sourceTable && sourceId),
        success: false,
      });

      // Check if error is related to dropdown fields
      let errorMessage = err.message || 'Failed to save record';

      try {
        // Try to parse backend error JSON
        const errorData = JSON.parse(err.message);
        const dropdownFields = DROPDOWN_FIELDS[tableType] || {};

        // Check if error fields are dropdown fields
        const dropdownFieldErrors = [];
        Object.keys(errorData).forEach(field => {
          if (dropdownFields[field]) {
            dropdownFieldErrors.push(dropdownFields[field]);
          }
        });

        if (dropdownFieldErrors.length > 0) {
          errorMessage =
            `The following dropdown fields are not currently available and cannot be populated: ${dropdownFieldErrors.join(', ')}. ` +
            'Please leave these fields empty.';
        } else if (typeof errorData === 'object') {
          // Show detailed backend errors if not dropdown-related
          const fieldErrors = Object.entries(errorData)
            .map(([field, messages]) => {
              const label = field.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
              const msg = Array.isArray(messages) ? messages.join(', ') : messages;
              return `${label}: ${msg}`;
            })
            .join('; ');
          errorMessage = fieldErrors || errorMessage;
        }
      } catch (parseErr) {
        // If parsing fails, use original error message
        console.log('Could not parse error as JSON:', parseErr);
      }

      setError(errorMessage);
      setSaving(false);
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  const handleBackToIndex = () => {
    navigate(`/projects/${projectId}/hart`);
  };

  const handleSubmit = () => {
    handleSave('details');
  };

  const handleAddNew = () => {
    handleSave('addNew');
  };

  if (!canCreateRecordType(tableType, projectId)) {
    return (
      <>
        <PageHeader title={`Add New ${recordLabel}`} />
        <Box sx={{ mt: 3, mb: 4 }}>
          <Alert severity="warning" sx={{ mb: 3 }}>
            You do not have permission to create new {recordLabel} records.
          </Alert>
          <Button variant="outlined" onClick={() => navigate(-1)} size="large">
            Go Back
          </Button>
        </Box>
      </>
    );
  }

  // Render the appropriate form based on tableType
  const renderForm = () => {
    const FormComponent = FORM_COMPONENTS[tableType];

    if (FormComponent) {
      return (
        <FormComponent
          data={formData}
          editable={true}
          onChange={setFormData}
          errors={validationErrors}
          isCreating={true}
          projectId={projectId}
        />
      );
    }

    // If form not implemented for this type
    return (
      <Typography variant="body1" color="text.secondary">
        Form for {recordLabel} not yet implemented.
      </Typography>
    );
  };

  return (
    <>
      <PageHeader
        title={`Add New ${recordLabel}`}
        subtitle={`Create a new ${recordLabel} record`}
      />

      <Box sx={{ mt: 3, mb: 4 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 3 }}>
            Record saved successfully! Redirecting...
          </Alert>
        )}

        <Card sx={{ boxShadow: 0 }}>
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h5" sx={{ mb: 3, fontWeight: 500 }}>
              {recordLabel} Details
            </Typography>

            {renderForm()}

            <Box sx={{ display: 'flex', gap: 2, mt: 4, pt: 3, borderTop: 1, borderColor: 'divider', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  variant="outlined"
                  onClick={handleBackToIndex}
                  disabled={saving}
                  size="large"
                >
                  Back to Home
                </Button>
                <Button
                  variant="outlined"
                  onClick={handleBack}
                  disabled={saving}
                  size="large"
                >
                  Back
                </Button>
              </Box>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={handleAddNew}
                  disabled={saving || !INITIAL_FORM_DATA[tableType]}
                  size="large"
                >
                  {saving ? 'Saving...' : `Submit & Add More New Data`}
                </Button>
                <Button
                  variant="contained"
                  onClick={handleSubmit}
                  disabled={saving || !INITIAL_FORM_DATA[tableType]}
                  size="large"
                >
                  {saving ? 'Saving...' : 'Submit'}
                </Button>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </>
  );
};

const routeArgs = {
  element: <AddNewDetailsPage />,
};

export default buildRoute(routeArgs);

